import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const LEAD_API = "https://fetc.in/api/v1/lead";

// Utility: Convert query params to URL string
const toQueryString = (params) => {
  if (!params) return "";
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((v) => {
        if (v !== undefined && v !== null && v !== "") {
          searchParams.append(`${key}[]`, v);
        }
      });
    } else if (value !== undefined && value !== null && value !== "") {
      searchParams.append(key, value);
    }
  });

  const query = searchParams.toString();
  return query ? `?${query}` : "";
};

export const leadApi = createApi({
  reducerPath: "leadApi",
  baseQuery: fetchBaseQuery({
    baseUrl: LEAD_API,
    credentials: "include",
    prepareHeaders: (headers, { getState }) => {
      const token = getState()?.auth?.token;
      if (token) headers.set("Authorization", `Bearer ${token}`);
      return headers;
    },
  }),
  tagTypes: ["Lead"],
  endpoints: (builder) => ({
    // Create a new lead
    createLead: builder.mutation({
      query: (formData) => ({
        url: "/create",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: [{ type: "Lead", id: "LIST" }],
      transformResponse: (response) => response.data || response,
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response.data?.message || "Failed to create lead",
      }),
    }),

    // Update a lead by ID
    editLeadById: builder.mutation({
      query: ({ id, data }) => ({
        url: `/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Lead", id },
        { type: "Lead", id: "LIST" },
      ],
      transformResponse: (response) => response.data || response,
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response.data?.message || "Failed to update lead",
      }),
    }),

    // Fetch all leads
    getAllLeads: builder.query({
      query: (params) => `/allleads${toQueryString(params)}`,
      providesTags: (result) =>
        result?.leads
          ? [
              ...result.leads.map(({ _id }) => ({ type: "Lead", id: _id })),
              { type: "Lead", id: "LIST" },
            ]
          : [{ type: "Lead", id: "LIST" }],
    }),

    // Fetch a single lead by ID
    getLeadById: builder.query({
      query: (id) => `/${id}`,
      providesTags: (result, error, id) => [{ type: "Lead", id }],
    }),

    // Single file upload
    uploadFiles: builder.mutation({
      query: (formData) => ({
        url: "/single",
        method: "POST",
        body: formData,
      }),
      transformResponse: (response) => ({
        fileUrl: response.fileUrl || null,
      }),
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response.data?.message || "Failed to upload file",
      }),
    }),

   uploadBulkLeads: builder.mutation({
  query: (leadsArray) => ({
    url: "/upload-bulk",
    method: "POST",
    headers: { "Content-Type": "application/json" }, // important!
    body: JSON.stringify({ leads: leadsArray }),
  }),
  invalidatesTags: [{ type: "Lead", id: "LIST" }],
  transformResponse: (response) => response, // returns { success: true, created: X }
  transformErrorResponse: (response) => ({
    status: response.status,
    message: response.data?.message || "Failed to upload bulk leads",
  }),
}),


    // Delete lead
    deleteLeadById: builder.mutation({
      query: (id) => ({
        url: `/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Lead", id },
        { type: "Lead", id: "LIST" },
      ],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response.data?.message || "Failed to delete lead",
      }),
    }),
  }),
});

// Export hooks
export const {
  useCreateLeadMutation,
  useEditLeadByIdMutation,
  useGetAllLeadsQuery,
  useGetLeadByIdQuery,
  useUploadFilesMutation,
  useUploadBulkLeadsMutation,
  useDeleteLeadByIdMutation,
} = leadApi;
